import { useEffect, useState } from "react";
import { Cake, Heart, Sparkles, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PRESET_AMOUNTS = [5, 10, 20, 50];

export default function BirthdayBanner({ compact = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null); // sibling key currently being supported
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    amount: 10,
    customAmount: "",
    name: "",
    email: "",
    message: "",
  });

  useEffect(() => {
    api.get("/birthday-status").then((r) => setData(r.data)).catch(() => {});
  }, []);

  // Allow any other component (e.g. About page profile cards, support
  // section) to open the tip modal for a specific sibling via a custom event.
  useEffect(() => {
    const handler = (e) => {
      const key = e.detail;
      // For Donja we don't show a birthday-tip flow — bail out
      if (key === "donja") return;
      // Resolve sibling object: prefer live data, fall back to a stub
      const sib = (data?.siblings || []).find((s) => s.key === key)
        || { key, display_name: key === "josiah" ? "Big Sib Josiah" : "Lil Sib Jayla", is_birthday_today: false, age: 0, days_until_birthday: 0 };
      setOpen(sib);
      setForm((f) => ({ ...f, amount: 10, customAmount: "" }));
    };
    window.addEventListener("bagdash-support-sibling", handler);
    return () => window.removeEventListener("bagdash-support-sibling", handler);
  }, [data]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  if (!data) return null;
  const active = (data.siblings || []).filter((s) => s.is_birthday_week);
  // Even when no banner needs to show, we still render the modal listener so
  // external "Support Big Sib" buttons can trigger checkout year-round.
  if (active.length === 0 && !open) return null;

  const startTip = (sib) => {
    setOpen(sib);
    setForm((f) => ({ ...f, amount: 10, customAmount: "" }));
  };
  const closeTip = () => setOpen(null);

  const submitTip = async (e) => {
    e.preventDefault();
    const amt = form.customAmount ? parseFloat(form.customAmount) : form.amount;
    if (!amt || amt <= 0) return toast.error("Enter a tip amount.");
    if (!form.name?.trim() || !form.email?.trim()) return toast.error("Please add your name and email.");
    setSubmitting(true);
    try {
      const r = await api.post("/birthday-tip/checkout", {
        sibling: open.key,
        amount: amt,
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        message: form.message?.trim() || "",
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-3" data-testid="birthday-banners">
        {active.map((sib) => (
          <SiblingCard key={sib.key} sib={sib} compact={compact} onSupport={() => startTip(sib)} />
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="birthday-modal">
          <div className="bg-white rounded-3xl border-4 border-amber-300 max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="font-fredoka font-bold text-xl text-slate-900 flex items-center gap-2">
                <Cake className="text-pink-500" strokeWidth={2.5} />
                Send {open.display_name} a Birthday Tip
              </div>
              <button onClick={closeTip} className="p-1 rounded-lg hover:bg-slate-100" data-testid="birthday-modal-close"><XIcon size={20}/></button>
            </div>
            <p className="text-sm text-slate-600 mb-4">Optional · processed securely by Stripe. 100% goes toward {open.display_name.replace("Big Sib ", "").replace("Lil Sib ", "")}&apos;s dreams.</p>

            <form onSubmit={submitTip} className="space-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Choose amount</div>
                <div className="grid grid-cols-4 gap-2">
                  {PRESET_AMOUNTS.map((a) => (
                    <button
                      type="button"
                      key={a}
                      onClick={() => setForm({ ...form, amount: a, customAmount: "" })}
                      className={`rounded-xl border-2 py-2 font-fredoka font-bold transition ${form.amount === a && !form.customAmount ? "bg-amber-300 border-amber-600 text-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-amber-300"}`}
                      data-testid={`birthday-amount-${a}`}
                    >
                      ${a}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Or custom amount"
                  value={form.customAmount}
                  onChange={(e) => setForm({ ...form, customAmount: e.target.value })}
                  className="input-clay h-11 px-3 w-full mt-2"
                  data-testid="birthday-custom-amount"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <input type="text" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-clay h-11 px-3 w-full" required data-testid="birthday-name" />
                <input type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-clay h-11 px-3 w-full" required data-testid="birthday-email" />
              </div>
              <textarea placeholder={`Birthday message for ${open.display_name} (optional)`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-clay px-3 py-2 w-full text-sm" rows="2" maxLength="500" data-testid="birthday-message" />
              <button type="submit" disabled={submitting} className="w-full btn-push btn-push-green py-3 font-fredoka font-bold text-base disabled:opacity-60" data-testid="birthday-submit">
                {submitting ? "Opening Stripe…" : `🎂 Send $${form.customAmount || form.amount} to ${open.display_name}`}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function SiblingCard({ sib, compact, onSupport }) {
  const isTodayBirthday = sib.is_birthday_today;
  const turning = sib.age + (sib.days_until_birthday > 0 ? 1 : 0);
  const dayPhrase = isTodayBirthday
    ? "🎉 Today is the day!"
    : sib.days_until_birthday > 0
      ? `${sib.days_until_birthday} day${sib.days_until_birthday === 1 ? "" : "s"} to go`
      : `${Math.abs(sib.days_until_birthday)} day${Math.abs(sib.days_until_birthday) === 1 ? "" : "s"} ago — still celebrating!`;

  return (
    <div
      className={`card-clay p-4 border-4 ${isTodayBirthday ? "border-pink-300 bg-gradient-to-r from-pink-50 via-amber-50 to-pink-50" : "border-amber-200 bg-amber-50"} relative overflow-hidden`}
      data-testid={`birthday-card-${sib.key}`}
    >
      {isTodayBirthday && (
        <div className="absolute -top-1 -right-1 text-2xl rotate-12 animate-bounce">🎈</div>
      )}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-pink-200 text-pink-700 flex items-center justify-center shrink-0">
          <Cake strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-fredoka font-bold text-slate-900 text-base sm:text-lg">
            {isTodayBirthday ? "🎂 Happy Birthday, " : "🎂 Birthday week for "}
            {sib.display_name}!
          </div>
          {!compact && (
            <p className="text-sm text-slate-700 mt-0.5">
              {sib.key === "jayla"
                ? "Help us celebrate her big day and support her dreams."
                : "Help us celebrate his big day and support his work, creativity, and leadership."}
            </p>
          )}
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 bg-white border border-amber-200 rounded-full px-2 py-0.5 font-semibold">
              <Sparkles size={11} strokeWidth={2.5} className="text-amber-500"/> Turning {turning}
            </span>
            <span>{sib.birthday_label} · {dayPhrase}</span>
          </div>
        </div>
        <button
          onClick={onSupport}
          className="btn-push btn-push-pink px-4 py-2 text-sm font-fredoka font-bold flex items-center gap-1 shrink-0"
          data-testid={`birthday-support-${sib.key}`}
        >
          <Heart size={14} strokeWidth={3} />
          Support {sib.display_name.replace("Big Sib ", "").replace("Lil Sib ", "")}
        </button>
      </div>
    </div>
  );
}
